import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import Select from '@/components/Select'
import { StyledTextField } from '@/components/StyleTextField'
import {
  useCreateProfileMutation,
  useGetCountryQuery,
  useGetProfileQuery,
} from '@/store/apiService'
import { REFRENCE_OPTIONS } from '@/utils/constants'

interface Profile {
  user: string
}

const Profile = () => {
  const [profileData, setProfileData] = useState({
    name: '',
    country: '',
    city: '',
    address1: '',
    postal_code: '',
    source: '',
    serial_number: '',
    vat: '',
  })
  const [errors, setErrors] = useState<any>({})
  const [terms, setTerms] = useState<boolean>(false)
  const { data: countryList } = useGetCountryQuery({})
  const { data: profileResponse, refetch } = useGetProfileQuery<Profile>()
  const [saveProfileData] = useCreateProfileMutation()

  useEffect(() => {
    if (!profileResponse) return

    setProfileData({
      name: profileResponse.name || '',
      country: profileResponse.country || '',
      address1: profileResponse.address1 || '',
      city: profileResponse.city || '',
      postal_code: profileResponse.postal_code || '',
      serial_number: profileResponse.serial_number || '',
      source: profileResponse.source || '',
      vat: profileResponse.vat || '',
    })
    setTerms(true)
  }, [profileResponse])

  const handleValidation = () => {
    const errors: Record<string, any> = {}
    let formIsValid = true

    if (!profileData.name) {
      formIsValid = false
      errors['name'] = 'Please enter your full name.'
    }

    if (!profileData.country || profileData.country === 'Select Country') {
      formIsValid = false
      errors['country'] = 'Please select country.'
    }

    if (!terms) {
      formIsValid = false
      errors['terms'] = 'Please agree to terms & conditions.'
    }

    setErrors(errors)
    return formIsValid
  }

  const handleChange = (event: any) => {
    const { name, value, checked } = event.target
    if (name === 'terms') {
      setTerms(checked)
    } else {
      setProfileData((prevState) => ({
        ...prevState,
        [name]: value.trim(),
      }))
    }
  }

  const saveProfile = async (event: any) => {
    event.preventDefault()

    if (handleValidation()) {
      try {
        const response = await saveProfileData(profileData).unwrap()

        if (response) {
          await refetch()
          window.dispatchEvent(new CustomEvent('profileUpdated'))
          toast.success('Profile Updated.')
        }
      } catch (error) {
        toast.error('Something went wrong.')
      }
    }
  }
  const countryId = useMemo(
    () => countryList?.find((c: any) => c.id === profileData.country)?.id,
    [countryList, profileData.country],
  )
  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        py: 3,
      }}
    >
      <Box
        width="clamp(400px, 50%, 800px)"
        sx={{
          maxWidth: '100%',
          px: 2,
        }}
      >
        <Box display="flex" justifyContent="center" mb={3}>
          <Typography fontSize={'36px'} fontWeight={600}>
            My Profile
          </Typography>
        </Box>

        <form onSubmit={saveProfile}>
          <Grid container spacing={2}>
            {/* Full Name */}
            <Grid size={12}>
              <StyledTextField
                id="fullName"
                label="Full Name"
                variant="outlined"
                size="medium"
                name="name"
                value={profileData.name}
                fullWidth
                onChange={handleChange}
                slotProps={{
                  input: {
                    style: {
                      color: 'white',
                      height: '56px',
                      padding: '0 10px',
                    },
                  },
                  inputLabel: { shrink: true },
                }}
              />
              <Typography color="error" variant="body2">
                {errors['name']}
              </Typography>
            </Grid>
            {/* Address */}
            <Grid size={12}>
              <StyledTextField
                id="address"
                label="Address"
                variant="outlined"
                size="medium"
                name="address1"
                value={profileData.address1}
                fullWidth
                onChange={handleChange}
                slotProps={{
                  input: {
                    style: {
                      color: 'white',
                      height: '56px',
                      padding: '0 10px',
                    },
                  },
                  inputLabel: { shrink: true },
                }}
              />
            </Grid>
            {/* City */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <StyledTextField
                id="city"
                label="City"
                variant="outlined"
                size="medium"
                name="city"
                value={profileData.city}
                fullWidth
                onChange={handleChange}
                slotProps={{
                  input: {
                    style: {
                      color: 'white',
                      height: '56px',
                      padding: '0 10px',
                    },
                  },
                  inputLabel: { shrink: true },
                }}
              />
            </Grid>
            {/* Postal Code */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <StyledTextField
                id="postalCode"
                label="Postal Code"
                variant="outlined"
                size="medium"
                name="postal_code"
                value={profileData.postal_code}
                fullWidth
                onChange={handleChange}
                slotProps={{
                  input: {
                    style: {
                      color: 'white',
                      height: '56px',
                      padding: '0 10px',
                    },
                  },
                  inputLabel: { shrink: true },
                }}
              />
            </Grid>
            {/* Country */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                key={countryId}
                name="country"
                defaultValue={countryId}
                onFormValueChange={(name, value) =>
                  setProfileData({ ...profileData, country: value })
                }
                options={countryList?.map((c: any) => ({
                  text: c.name,
                  value: c.id,
                  disabled: false,
                }))}
                outlinedLabel={true}
                title="Country"
                inputLabel={{
                  color: '#999999',
                  '&.Mui-focused': {
                    color: '#ffffff',
                  },
                }}
                sx={{
                  backgroundColor: '#000',
                  height: '56px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#999',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
              <Typography color="error" variant="body2">
                {errors['country']}
              </Typography>
            </Grid>
            {/* Vat */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <StyledTextField
                id="vat"
                label="Vat (Optional)"
                variant="outlined"
                size="medium"
                name="vat"
                value={profileData.vat}
                fullWidth
                onChange={handleChange}
                slotProps={{
                  input: {
                    style: {
                      color: 'white',
                      height: '56px',
                      padding: '0 10px',
                    },
                  },
                  inputLabel: { shrink: true },
                }}
              />
            </Grid>
            {/* Serial Number */}
            <Grid size={12}>
              <StyledTextField
                id="serialNumber"
                label="Optishade Serial Number"
                variant="outlined"
                size="medium"
                name="serial_number"
                value={profileData.serial_number}
                fullWidth
                onChange={handleChange}
                slotProps={{
                  input: {
                    style: {
                      color: 'white',
                      height: '56px',
                      padding: '0 10px',
                    },
                  },
                  inputLabel: { shrink: true },
                }}
              />
            </Grid>
            {/* Profile Source */}
            <Grid size={12}>
              <Select
                key={profileData.source}
                name="profile source"
                defaultValue={profileData.source}
                onFormValueChange={(name, value) =>
                  setProfileData({ ...profileData, source: value })
                }
                options={REFRENCE_OPTIONS}
                outlinedLabel={true}
                title="How did you find about Matisse Software?"
                inputLabel={{
                  color: '#999999',
                  '&.Mui-focused': {
                    color: '#ffffff',
                  },
                }}
                sx={{
                  backgroundColor: '#000',
                  height: '56px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#999',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
            </Grid>
            {/* Terms & Conditions */}
            <Grid size={12}>
              <FormControlLabel
                sx={{ pl: 0.5, py: 3 }}
                control={
                  <Checkbox
                    id="checkbox"
                    name="terms"
                    checked={terms}
                    onChange={handleChange}
                    disabled={true}
                  />
                }
                label={
                  <Typography variant="body2" component="span">
                    {'I accept the '}
                    <a
                      href="terms_conditions.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: 'none',
                        color: '#0D6EFD',
                        marginRight: '0.3rem',
                      }}
                    >
                      {'Terms or Conditions'}
                    </a>
                    {'of service and '}
                    <a
                      href="data_agreement.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: '#0D6EFD' }}
                    >
                      {'Data processing agreement.'}
                    </a>
                  </Typography>
                }
              />
              <Typography color="error" variant="body2">
                {errors['terms']}
              </Typography>
            </Grid>
            {/* Submit Button */}
            <Grid size={12}>
              <Button
                type="submit"
                label={'Submit'}
                size="large"
                sx={{ bgcolor: '#0D6EFD', color: 'white' }}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  )
}

export default Profile
